import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { YOUTUBE_URL, YoutubeLogo } from '../components/community_links';

/** Une vidéo telle que la sert la fonction `youtubeVideos` (functions/src/youtube/feed.ts). */
interface YoutubeVideo {
  id: string;
  title: string;
  published: string;
  summary: string;
  views: number;
  lang: string | null;
  short: boolean;
  /** Vignette paysage ; rognée au centre, elle donne l'image 9:16. */
  thumb?: string;
}

interface YoutubePage {
  videos: YoutubeVideo[];
  next: string | null;
}

/**
 * La vignette d'une vidéo : la paysage que donne l'API, rognée au centre par
 * `object-cover`. YouTube y centre la vignette personnalisée d'un Short sur
 * un fond flouté, si bien que le centre EST l'image 9:16 choisie. La
 * verticale `oar2` ne convient pas : YouTube la choisit seul, et ignore la
 * vignette personnalisée.
 */
const thumbnail = (v: YoutubeVideo) => v.thumb || `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`;

const watchUrl = (v: YoutubeVideo) =>
  v.short ? `https://www.youtube.com/shorts/${v.id}` : `https://www.youtube.com/watch?v=${v.id}`;

const formatDate = (iso: string, locale: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
};

const VideoCard: React.FC<{ video: YoutubeVideo; locale: string }> = ({ video, locale }) => {
  const { t } = useTranslation();

  // La carte entière est un lien vers YouTube, dans un nouvel onglet : le
  // lecteur intégré, coincé dans une petite vignette, n'apportait rien.
  // C'est aussi ce lien que suivent les robots.
  return (
    <a
      href={watchUrl(video)}
      target="_blank"
      rel="noopener"
      aria-label={`${t('videos.play', 'Lire la vidéo')} : ${video.title}`}
      className="group bg-white rounded-xl shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow"
    >
      <div className="relative aspect-[9/16] bg-amber-950">
        <img
          src={thumbnail(video)}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            const img = e.currentTarget;
            const fallback = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;
            if (img.src !== fallback) img.src = fallback;
          }}
        />
        <span className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/25 transition-colors">
          <span className="h-10 w-10 rounded-full bg-black/55 flex items-center justify-center backdrop-blur-sm">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white" aria-hidden="true">
              <polygon points="7 4 20 12 7 20 7 4" />
            </svg>
          </span>
        </span>
        {video.lang && video.lang !== locale && (
          <span className="absolute top-2 left-2 rounded-full bg-white/90 px-2 py-0.5 text-[0.65rem] font-bold uppercase text-amber-900">
            {video.lang}
          </span>
        )}
      </div>
      <div className="p-2 flex flex-col gap-1 flex-1">
        <span className="font-serif text-xs font-bold text-amber-900 leading-snug group-hover:underline line-clamp-2">
          {video.title}
        </span>
        <span className="mt-auto pt-1 text-[0.65rem] text-amber-700">
          {formatDate(video.published, locale)}
          {video.views > 0 && ` · ${t('videos.views', { count: video.views, defaultValue: '{{count}} vues' })}`}
        </span>
      </div>
    </a>
  );
};

const Videos: React.FC = () => {
  const { lang } = useParams();
  const locale = lang || 'fr';
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [videos, setVideos] = useState<YoutubeVideo[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async (page?: string) => {
    // `no-cache` : toujours redemander, jamais la copie du navigateur. Un
    // rechargement forcé (Ctrl+F5) ne s'applique pas aux fetch() de la page,
    // et une vidéo publiée restait invisible tant que la copie n'expirait pas.
    // La fonction garde sa propre réponse 5 minutes : YouTube n'est pas
    // sollicité pour autant.
    const res = await fetch(`/api/youtube${page ? `?page=${encodeURIComponent(page)}` : ''}`, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as YoutubePage;
  }, []);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((p) => {
        if (cancelled) return;
        setVideos(p.videos);
        setNext(p.next);
      })
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [load]);

  const loadMore = async () => {
    if (!next) return;
    setLoadingMore(true);
    try {
      const p = await load(next);
      // Une page peut chevaucher la précédente si une vidéo paraît entre-temps.
      setVideos((prev) => [...prev, ...p.videos.filter((v) => !prev.some((x) => x.id === v.id))]);
      setNext(p.next);
    } catch {
      setError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const title = t('videos.title', 'Vidéos');

  return (
    <>
      <Helmet>
        <title>{title} — Bible4Me</title>
        <meta name="description" content={String(t('seo.videos.description', 'Les vidéos de la chaîne YouTube Bible4Me : méditations de l\'Évangile et nouveautés de l\'application.'))} />
        <link rel="canonical" href={`https://bible4.me/${locale}/videos`} />
      </Helmet>
      <div className="min-h-screen bg-amber-100 text-amber-900">
        <div className="sticky top-0 z-20 bg-amber-200 px-4 py-3 shadow-sm">
          <button
            type="button"
            onClick={() => navigate(`/${locale}`)}
            className="flex items-center gap-2 hover:opacity-70 transition-opacity"
          >
            <img src="/logo%20626x626.png" alt="Bible4Me" className="w-6 h-6 rounded-md object-contain" />
            <span className="font-bold text-amber-900 text-sm">Bible4Me</span>
          </button>
        </div>

        <div className="px-4 pt-4 pb-5 bg-amber-800 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-white font-serif text-xl font-normal mb-1">{title}</h1>
            <p className="text-amber-200 text-xs">{t('videos.subtitle', 'La chaîne YouTube de Bible4Me')}</p>
          </div>
          <a
            href={`${YOUTUBE_URL}?sub_confirmation=1`}
            target="_blank"
            rel="noopener"
            className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-[#ff0000] px-3 py-1.5 text-sm font-bold text-white hover:bg-[#cc0000] transition-colors"
          >
            <YoutubeLogo className="h-4 w-4" />
            {t('videos.subscribe', "S'abonner")}
          </a>
        </div>
        <div className="h-3 bg-amber-800 rounded-b-3xl" />

        <div className="max-w-5xl mx-auto px-3 py-5 pb-12">
          {loading && <div className="text-center text-sm text-amber-700">{t('common.loading', 'Chargement…')}</div>}
          {!loading && error && videos.length === 0 && (
            <div className="text-center text-sm text-amber-700">
              {t('videos.error', 'Les vidéos ne se chargent pas.')}{' '}
              <a href={YOUTUBE_URL} target="_blank" rel="noopener" className="underline font-semibold">
                {t('videos.openChannel', 'Ouvrir la chaîne sur YouTube')}
              </a>
            </div>
          )}

          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {videos.map((v) => (
              <VideoCard key={v.id} video={v} locale={locale} />
            ))}
          </div>

          {next && (
            <div className="text-center mt-6">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="bg-amber-800 text-amber-50 rounded-xl py-2 px-6 text-sm font-semibold hover:bg-amber-900 transition-colors disabled:opacity-50"
              >
                {loadingMore ? t('common.loading', 'Chargement…') : t('videos.loadMore', 'Voir plus')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Videos;
